<template>
    <div>
      <div class="p-6 pb-0">
        <BreadCrumbs :navigation="navigation" />
      </div>

      <div class="flex justify-between items-center p-6 pb-0">
        <h2 class="text-lg font-semibold">{{ $t('pv.confronto.title') }}</h2>
        <div class="flex gap-2">
          <button
            class="px-3 py-1.5 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
            :disabled="bulkVerifying"
            @click="verifyAllPage">
            <span v-if="!bulkVerifying">{{ $t('pv.confronto.verify_page_button') }}</span>
            <span v-else>{{ $t('pv.confronto.verifying_in_progress') }} {{ bulkProgress.done }}/{{ bulkProgress.total }}</span>
          </button>
        </div>
      </div>


      <div class="p-6 pt-0 mt-2">
        <PageServerTable
        :headers="headers"
        :items="items"
        :sort-by="sortby"
        :reload-fn="reload"
        :items-length="count"
        item-value="CustSupp"
        show-current-page
        :hide-default-footer="true"
        show-expand
        >
          <template #item.verificationStatus="{ item }">
            <span :class="globalBadgeClass(item)">{{ globalBadgeText(item) }}</span>
          </template>

          <!-- Contenuto dell'accordion espanso -->
          <template #expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="bg-gray-50 p-4">
                <table class="min-w-full text-sm border bg-white">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="px-3 py-2 text-left w-32">{{ $t('pv.confronto.th_branch') }}</th>
                      <th class="px-3 py-2 text-left">{{ $t('pv.confronto.th_registered_address') }}</th>
                      <th class="px-3 py-2 text-left">{{ $t('pv.confronto.th_google_address') }}</th>
                      <th class="px-3 py-2 text-left w-24">{{ $t('pv.confronto.th_status') }}</th>
                      <th class="px-3 py-2 w-32"></th>
                      <th class="px-3 py-2 w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Indirizzo del CLIENTE come prima riga -->
                    <tr class="border-t bg-blue-50/40">
                      <td class="px-3 py-2">
                        <span class="ml-2">{{ rowIcon(item.matchStatus) }}</span>
                        {{ $t('pv.confronto.main_branch') }}
                      </td>
                      <td class="px-3 py-2">
                        <div class="flex flex-wrap gap-x-3 gap-y-1.5">
                          <div v-for="part in addressParts(item)" :key="part.key" class="flex flex-col">
                            <span class="text-[9px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">{{ part.label }}</span>
                            <span :class="chipClass(partStatus(item, part.key))">{{ part.value }}</span>
                          </div>
                        </div>
                      </td>
                      <td class="px-3 py-2">
                        <template v-if="item.googleComponents">
                          <div class="flex flex-wrap gap-x-3 gap-y-1.5">
                            <div v-for="part in googleParts(item)" :key="part.key" class="flex flex-col">
                              <span class="text-[9px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">{{ part.label }}</span>
                              <span :class="chipClass(partStatus(item, part.key))">{{ part.value }}</span>
                            </div>
                          </div>
                          <span v-if="item.matchStatus === 'partial'" class="mt-1 block text-xs text-yellow-700">{{ $t('pv.confronto.partial_match') }}</span>
                        </template>
                        <span v-else-if="item.matchStatus === 'not_found'" class="text-gray-500 italic text-xs">{{ $t('pv.confronto.address_not_found') }}</span>
                        <span v-else-if="item.matchStatus === 'error'" class="text-red-600 italic text-xs">{{ $t('pv.confronto.error_prefix') }} {{ item.googleError || $t('pv.confronto.verification_failed') }}</span>
                        <span v-else class="text-gray-400 italic text-xs">{{ $t('pv.confronto.to_verify') }}</span>
                      </td>
                      <td class="px-3 py-2 text-right">
                        <button
                          class="text-blue-600 hover:underline disabled:opacity-50"
                          :disabled="item.verifying"
                          @click="verify(item)"
                        >
                          {{ item.verifying ? '…' : $t('pv.confronto.verify') }}
                        </button>
                      </td>
                      <td class="px-3 py-2 text-center">
                        <a v-if="item.googleAddress" :href="mapsUrl(item.googleAddress)" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-600 transition-colors" title="Apri su Google Maps">
                          <v-icon name="md-language-round" scale="1.2" />
                        </a>
                      </td>
                      <td class="px-3 py-2 text-center">
                        <button type="button" class="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1 rounded text-sm transition-colors disabled:opacity-50" :disabled="!item.matchStatus || item.validating || item.validated" @click="validateAddress(item)">
                          {{ item.validated ? 'Convalidato' : (item.validating ? '…' : 'Convalida') }}
                        </button>
                      </td>
                    </tr>

                    <!-- Sedi secondarie -->
                    <tr v-for="b in item.branches" :key="b.Branch" class="border-t">
                      <td class="px-3 py-2">
                        <span class="ml-2">{{ rowIcon(b.matchStatus) }}</span>
                        {{ $t('pv.confronto.branch_label') }} {{ b.Branch }}
                      </td>
                      <td class="px-3 py-2">
                        <div class="flex flex-wrap gap-x-3 gap-y-1.5">
                          <div v-for="part in addressParts(b)" :key="part.key" class="flex flex-col">
                            <span class="text-[9px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">{{ part.label }}</span>
                            <span :class="chipClass(partStatus(b, part.key))">{{ part.value }}</span>
                          </div>
                        </div>
                      </td>
                      <td class="px-3 py-2">
                        <template v-if="b.googleComponents">
                          <div class="flex flex-wrap gap-x-3 gap-y-1.5">
                            <div v-for="part in googleParts(b)" :key="part.key" class="flex flex-col">
                              <span class="text-[9px] text-gray-400 uppercase tracking-wider leading-none mb-0.5">{{ part.label }}</span>
                              <span :class="chipClass(partStatus(b, part.key))">{{ part.value }}</span>
                            </div>
                          </div>
                          <span v-if="b.matchStatus === 'partial'" class="mt-1 block text-xs text-yellow-700">{{ $t('pv.confronto.partial_match') }}</span>
                        </template>
                        <span v-else-if="b.matchStatus === 'not_found'" class="text-gray-500 italic text-xs">{{ $t('pv.confronto.address_not_found') }}</span>
                        <span v-else-if="b.matchStatus === 'error'" class="text-red-600 italic text-xs">{{ $t('pv.confronto.error_prefix') }} {{ b.googleError || $t('pv.confronto.verification_failed') }}</span>
                        <span v-else class="text-gray-400 italic text-xs">{{ $t('pv.confronto.to_verify') }}</span>
                      </td>
                      <td class="px-3 py-2 text-right">
                        <button
                          class="text-blue-600 hover:underline disabled:opacity-50"
                          :disabled="b.verifying"
                          @click="verify(b)"
                        >
                          {{ b.verifying ? '…' : $t('pv.confronto.verify') }}
                        </button>
                      </td>
                      <td class="px-3 py-2 text-center">
                        <a v-if="b.googleAddress" :href="mapsUrl(b.googleAddress)" target="_blank" rel="noopener noreferrer" class="text-blue-400 hover:text-blue-600 transition-colors" title="Apri su Google Maps">
                          <v-icon name="md-language-round" scale="1.2" />
                        </a>
                      </td>
                      <td class="px-3 py-2 text-center">
                        <button type="button" class="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1 rounded text-sm transition-colors disabled:opacity-50" :disabled="!b.matchStatus || b.validating || b.validated" @click="validateAddress(b)">
                          {{ b.validated ? 'Convalidato' : (b.validating ? '…' : 'Convalida') }}
                        </button>
                      </td>
                     
                    </tr>

                    <tr v-if="item.branches.length === 0">
                      <td colspan="6" class="px-3 py-2 text-gray-500 italic text-center">
                        {{ $t('pv.confronto.no_secondary_branches') }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="mt-3 flex justify-end gap-2">
                  <button class="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                          @click="verifyAll(item)">
                    {{ $t('pv.confronto.verify_all_branches') }}
                  </button>
                  
                </div>
              </td>
            </tr>
          </template>

          <template #item.data-table-expand="{ internalItem, isExpanded, toggleExpand }">
            <v-icon
              :name="isExpanded(internalItem) ? 'md-keyboardarrowup-round' : 'md-keyboardarrowdown-round'"
              scale="1.2"
              class="cursor-pointer text-gray-700 hover:text-black"
              @click="toggleExpand(internalItem)"
            />
          </template>
          <template #bottom>
            <div class="flex justify-center py-3 border-t border-gray-200">
              <v-pagination
                :model-value="page"
                :length="Math.ceil(count / ipp)"
                :total-visible="7"
                @update:model-value="reload(undefined, undefined, $event)"
              />
            </div>
          </template>  
        </PageServerTable>


        
      </div>
      
      
      
    </div>
  </template>
  
  <script>
  import { router } from '@inertiajs/vue3';
  import BreadCrumbs from '@/Components/BreadCrumbs.vue';
  import PageServerTable from '@/Components/PageServerTable.vue';
  import { VPagination } from 'vuetify/components';
  
  export default {
    props: ['items', 'count', 'headers', 'sortby', 'ipp', 'ippo', 'page'],
    components: {
      BreadCrumbs,
      PageServerTable,
      VPagination
    },
    data() {
      return {
        navigation: [
          { name: 'Dashboard', href: route('dashboard') },
          { name: this.$t('modules.percorsi-vettori'), href: route('modules.home', 'percorsi-vettori') },
          { name: this.$t('modules.confronto-stradari'), href: '#' }
        ],
        bulkVerifying: false,
        bulkProgress: { done: 0, total: 0 },
      };
    },
    methods: {
      reload(sortby, itemsperpage, page) {
        router.post(window.location.href, {
          headers: this.headers,
          sortby: sortby ?? this.sortby,
          itemsperpage: itemsperpage ?? this.ipp,
          page: page ?? this.page,
        }, { only: ['items', 'count', 'headers', 'sortby', 'ipp', 'page'], preserveState: true });
      },

      formatAddress(x) {
        return [x.Address, x.ZIPCode, x.City, x.County, x.Country]
          .filter(Boolean).join(', ').toUpperCase();
      },

      addressParts(x) {
        return [
          { key: 'address', label: this.$t('pv.confronto.label_via'),     value: x.Address  ? x.Address.toUpperCase()  : null },
          { key: 'zipCode', label: this.$t('pv.confronto.label_cap'),     value: x.ZIPCode  ? x.ZIPCode.toUpperCase()  : null },
          { key: 'city',    label: this.$t('pv.confronto.label_city'),    value: x.City     ? x.City.toUpperCase()     : null },
          { key: 'county',  label: this.$t('pv.confronto.label_county'),  value: x.County   ? x.County.toUpperCase()   : null },
          { key: 'country', label: this.$t('pv.confronto.label_country'), value: x.Country  ? x.Country.toUpperCase()  : null },
        ].filter(p => p.value);
      },

      googleParts(entity) {
        const c = entity.googleComponents;
        if (!c) return [];
        const county = c.countyCode ?? c.county;
        const city = entity.resolvedCity ?? this.getMatchedCity(entity);
        const address = this.getFullAddress(entity);
        return [
          { key: 'address', label: this.$t('pv.confronto.label_via'),     value: address   ? address.toUpperCase()   : null },
          { key: 'zipCode', label: this.$t('pv.confronto.label_cap'),     value: c.zipCode ? c.zipCode.toUpperCase() : null },
          { key: 'city',    label: this.$t('pv.confronto.label_city'),    value: city      ? city.toUpperCase()      : null },
          { key: 'county',  label: this.$t('pv.confronto.label_county'),  value: county    ? county.toUpperCase()    : null },
          { key: 'country', label: this.$t('pv.confronto.label_country'), value: c.country ? c.country.toUpperCase() : null },
        ].filter(p => p.value);
      },

      getFullAddress(entity) {
        const c = entity.googleComponents;
        if (!c) return null;
        
        const magoAddress = (entity.Address ?? '').trim();
        const googleAddress = c.address ?? null;
        
        if (!magoAddress || !googleAddress) return googleAddress;
        
        const magoCivico = this.extractCivico(magoAddress);
        if (magoCivico) {
          const googleRoute = c.route ?? '';
          return googleRoute ? `${googleRoute} ${magoCivico}` : googleAddress;
        }
        
        return googleAddress;
      },

      extractCivico(address) {
        if (!address) return '';
        const match = address.match(/(\d+[a-z0-9]*(?:[\s\/][a-z0-9]+)*)\s*$/i);
        return match ? match[1] : '';
      },

      getMatchedCity(entity) {
        const c = entity.googleComponents;
        const magoCity = (entity.City ?? '').trim();
        if (!c || !magoCity) return c?.city ?? null;

        if (this.norm(magoCity) === this.norm(c.town ?? '')) {
          return c.town;
        }

        if (this.norm(magoCity) === this.norm(c.city ?? '')) {
          return c.city;
        }

        const formatted = entity.googleAddress ?? '';
        if (this.isCityInFormatted(c.town ?? '', formatted)) {
          return c.town;
        }

        if (this.isCityInFormatted(c.city ?? '', formatted)) {
          return c.city;
        }

        return c.town ?? c.city ?? null;
      },

      isCityInFormatted(city, formatted) {
        if (!city || !formatted) return false;
        return this.norm(formatted).includes(this.norm(city));
      },

      levenshteinDistance(a, b) {
        const m = a.length;
        const n = b.length;
        const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

        for (let i = 0; i <= m; i++) dp[i][0] = i;
        for (let j = 0; j <= n; j++) dp[0][j] = j;

        for (let i = 1; i <= m; i++) {
          for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(
              dp[i - 1][j] + 1,
              dp[i][j - 1] + 1,
              dp[i - 1][j - 1] + cost
            );
          }
        }

        return dp[m][n];
      },

      norm(s) {
        if (!s) return '';
        let normalized = s.toLowerCase().trim();
        if (String.prototype.normalize) {
          normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        }
        return normalized.replace(/[,'"`\.]+/g, ' ').replace(/\s+/g, ' ').trim();
      },

      stripCivico(s) {
        if (!s) return '';
        return s.replace(/[,\s]+(\d+[a-z0-9]*(?:[\s\/][a-z0-9]+)*|snc)\s*$/i, '').trim();
      },

      partStatus(entity, key) {
        if (!entity.matchStatus) return 'pending';
        const c = entity.googleComponents;
        if (!c) return 'pending';
        
        let m = null;
        let g = null;
        
        if (key === 'county') {
          m = (entity.County ?? '').trim();
          g = c.countyCode ?? c.county;
        } else if (key === 'city') {
          m = (entity.City ?? '').trim();
          g = entity.resolvedCity ?? this.getMatchedCity(entity);
        } else if (key === 'address') {
          m = entity.Address;
          g = this.getFullAddress(entity);
        } else {
          const magoMap   = { zipCode: entity.ZIPCode, country: entity.Country };
          const googleMap = { zipCode: c.zipCode,      country: c.country };
          m = magoMap[key];
          g = googleMap[key];
        }
        
        if (!m || !g) return 'missing';
        if (key === 'address') {
          return this.norm(this.stripCivico(m)) === this.norm(this.stripCivico(g)) ? 'match' : 'mismatch';
        }
        return this.norm(m) === this.norm(g) ? 'match' : 'mismatch';
      },

      mapsUrl(address) {
        return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(address);
      },

      chipClass(status) {
        const base = 'px-1.5 py-0.5 rounded text-xs border font-mono';
        if (status === 'match')    return `${base} bg-green-50 border-green-300 text-green-900`;
        if (status === 'mismatch') return `${base} bg-red-50 border-red-300 text-red-900`;
        return `${base} bg-gray-100 border-gray-200 text-gray-700`;
      },

      async verify(entity, parent = null) {
        entity.verifying = true;
        try {
          const { data } = await axios.post(
            route('modules.percorsi-vettori.confronto-stradari.verify'),
            {
              custSupp: parent ? parent.CustSupp : entity.CustSupp,
              branch:   parent ? entity.Branch : null,
              address:  entity.Address,
              zipCode:  entity.ZIPCode,
              city:     entity.City,
              county:   entity.County,
              country:  entity.Country,
            }
          );
          entity.googleAddress    = data.formatted ? data.formatted.toUpperCase() : data.formatted;
          entity.googleComponents = data.components;
          entity.matchStatus      = data.matchStatus;
          entity.resolvedCity     = data.resolvedCity ?? null;
          entity.googleError      = data.error || null;
        } catch (e) {
          entity.matchStatus = 'error';
          entity.googleError = e.response?.data?.error || e.message || this.$t('pv.confronto.unknown_error');
          console.error('Verify error', e);
        } finally {
          entity.verifying = false;
        }
      },

      async verifyAll(item) {
        const queue = [];
        if (!item.matchStatus) queue.push({ entity: item, parent: null });
        for (const b of item.branches) {
          if (!b.matchStatus) queue.push({ entity: b, parent: item });
        }
        if (!queue.length) return;

        this.bulkProgress = { done: 0, total: queue.length };
        this.bulkVerifying = true;

        const CONCURRENCY = 5;
        let i = 0;
        const workers = Array.from({ length: CONCURRENCY }, async () => {
          while (i < queue.length) {
            const job = queue[i++];
            await this.verify(job.entity, job.parent);
            this.bulkProgress.done++;
          }
        });
        await Promise.all(workers);
        this.bulkVerifying = false;
      },


      async verifyAllPage() {
        const queue = [];
        for (const it of this.items) {
          if (!it.matchStatus) queue.push({ entity: it, parent: null });
          for (const b of it.branches) {
            if (!b.matchStatus) queue.push({ entity: b, parent: it });
          }
        }
        if (!queue.length) return;

        this.bulkProgress = { done: 0, total: queue.length };
        this.bulkVerifying = true;

        const CONCURRENCY = 5;
        let i = 0;
        const workers = Array.from({ length: CONCURRENCY }, async () => {
          while (i < queue.length) {
            const job = queue[i++];
            await this.verify(job.entity, job.parent);
            this.bulkProgress.done++;
          }
        });
        await Promise.all(workers);
        this.bulkVerifying = false;
      },


      badgeClass(status) {
        const base = 'px-2 py-0.5 rounded text-xs font-medium';
        switch (status) {
          case 'ok':        return `${base} bg-green-100 text-green-800`;
          case 'partial':   return `${base} bg-yellow-100 text-yellow-800`;
          case 'mismatch':  return `${base} bg-red-100 text-red-800`;
          case 'not_found': return `${base} bg-gray-200 text-gray-700`;
          case 'error':     return `${base} bg-red-200 text-red-900`;
          default:          return `${base} bg-gray-100 text-gray-500`;
        }
      },
      badgeText(status) {
        return ({
          ok: this.$t('pv.confronto.badge_ok'),
          partial: this.$t('pv.confronto.badge_partial'),
          mismatch: this.$t('pv.confronto.badge_mismatch'),
          not_found: this.$t('pv.confronto.badge_not_found'),
          error: this.$t('pv.confronto.badge_error'),
        })[status] || this.$t('pv.confronto.badge_to_verify');
      },

      globalBadgeClass(item) {
        const all = [item, ...item.branches];
        if (all.every(e => !e.matchStatus)) return this.badgeClass(null);

        const hasError    = all.some(e => ['error', 'http_error', 'exception', 'missing_api_key'].includes(e.matchStatus));
        const hasMismatch = all.some(e => ['mismatch', 'partial', 'not_found'].includes(e.matchStatus));
        const allDone     = all.every(e => e.matchStatus);

        if (hasError)    return this.badgeClass('error');
        if (hasMismatch) return this.badgeClass('mismatch');
        if (allDone)     return this.badgeClass('ok');
        return this.badgeClass(null);
      },

      globalBadgeText(item) {
        const all = [item, ...item.branches];
        const total = all.length;
        const done  = all.filter(e => e.matchStatus).length;

        if (done === 0) return this.$t('pv.confronto.badge_global_to_verify');

        const issues = all.filter(e =>
          ['mismatch', 'partial', 'not_found', 'error'].includes(e.matchStatus)
        ).length;

        if (done < total)   return this.$t('pv.confronto.badge_global_verified', { done, total });
        if (issues === 0)   return this.$t('pv.confronto.badge_global_all_ok', { total });
        return this.$t('pv.confronto.badge_global_discrepancies', { issues, total });
      },

      rowIcon(status) {
        if (!status) return '';                                    // non verificato
        if (status === 'ok') return '✅';                          // OK
        return '❌';                                                // tutto il resto = NOT OK
      },


      async validateAddress(entity) {
        if (!entity) return;

        entity.validating = true;
        try {
          await axios.post(
            route('modules.percorsi-vettori.confronto-stradari.validate'),
            {
              custSupp: entity.CustSupp,
              branch:   entity.Branch ?? null,
            }
          );
          entity.validated = true;
        } catch (e) {
          console.error('Validate error', e);
        } finally {
          entity.validating = false;
        }
      }
    }, 
    
    






  };


  
</script>

  